import React, { FC } from 'react';

import { HStack, Text, Tooltip, Wrap, WrapItem } from '@chakra-ui/react';
import { sortBy } from 'lodash';
import { useDispatch } from 'react-redux';

import {
  CheckmarkIcon,
  CloseIcon,
  LockCloseIcon,
  MinusIcon,
} from 'components/Icons';
import { actions } from 'containers/ActiveCloudResource/slice';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';

import { CheckStatus, DynamicAttribute, checkStatus } from './utils';

const STYLE: Record<
  CheckStatus,
  { icon: FC<any>; color: string; bg: string; rank: number }
> = {
  fail: { icon: CloseIcon, color: 'critical', bg: 'red.50', rank: 0 },
  pass: { icon: CheckmarkIcon, color: 'green.600', bg: 'green.50', rank: 1 },
  unknown: { icon: MinusIcon, color: 'gray.500', bg: 'gray.50', rank: 2 },
  unavailable: {
    icon: LockCloseIcon,
    color: 'gray.500',
    bg: 'gray.50',
    rank: 3,
  },
};

const tooltip = (attribute: DynamicAttribute, status: CheckStatus) => {
  switch (status) {
    case 'fail':
      return `Failing${
        attribute.rule ? ` (${attribute.rule})` : ''
      } — view in Issues`;
    case 'unknown':
      return 'Not assessed: Ariksa could not read this setting';
    case 'unavailable':
      return attribute.unavailable_reason;
    default:
      return 'Passing';
  }
};

export const CheckBadges: FC<{ checks: DynamicAttribute[] }> = ({ checks }) => {
  const dispatch = useDispatch();
  const ordered = sortBy(checks, c => STYLE[checkStatus(c)].rank);

  return (
    <Wrap spacing={2}>
      {ordered.map(attribute => {
        const status = checkStatus(attribute);
        const { icon: Icon, color, bg } = STYLE[status];
        const failing = status === 'fail';
        return (
          <WrapItem key={attribute.display_name}>
            <Tooltip label={tooltip(attribute, status)} hasArrow>
              <HStack
                as={failing ? 'button' : 'div'}
                spacing={1}
                px={2}
                py={1}
                borderRadius="full"
                bg={bg}
                color={color}
                fontSize="xs"
                transition="transform 0.15s"
                _hover={failing ? { transform: 'translateY(-1px)' } : undefined}
                onClick={
                  failing
                    ? () =>
                        dispatch(actions.setActiveTabName(MetadataTabs.Alerts))
                    : undefined
                }
              >
                <Icon w={3} h={3} />
                <Text>{attribute.display_name}</Text>
              </HStack>
            </Tooltip>
          </WrapItem>
        );
      })}
    </Wrap>
  );
};
